// @ts-strict-ignore
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { ProjectVO } from '../model/vo/project.vo';
import { IOConsumptionResultVO, ValidationContextVO, ValidationResultVO } from '../model/vo/validation-context.vo';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ProjectModel } from '../model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Injectable } from '@angular/core';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { ProjectUtils } from '../components/project/util/project-utils';
import { GlobalContainer } from '@3d-scene/model3d/global-container';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  private readonly validateProjectUrl: string = 'validation/validate-project';
  private readonly calcIOConsumptionUrl: string = 'validation/calc-consumption';
  private readonly quickFixUrl: string = 'validation/quick-fix';

  constructor(
    private modelConversionService: ModelConversionService,
    private wagoApiClient: WagoApiClient,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private articleContainerUtils: ArticleContainerUtils,
    private globalContainer: GlobalContainer
  ) {}

  public quickFix(articleNos: string[]): Observable<string[]> {
    return this.wagoApiClient
      .post<string[]>(this.quickFixUrl, articleNos);
  }

  public async validateCurrentProject(): Promise<ValidationResultVO> {
    this.updateRepresentationOfCurrentProjectForValidationPurposes();
    return await firstValueFrom(this.validateProject(
        this.projectModel.currentProject,
        new ValidationContextVO(
          this.localeResourceManager.currentLanguage.langKey, this.projectModel.currentProjectType
        )
      ))
  }

  public validateProject(
    projectVO: ProjectVO,
    validationContextVO: ValidationContextVO,
    handleError: boolean = true
  ): Observable<ValidationResultVO> {
    const validationProjectDTO: any = {
      projectVO,
      validationContextVO,
    };
    const params = { isErrorHandlingRequired: handleError };
    return this.wagoApiClient
      .post(this.validateProjectUrl, validationProjectDTO, null, params)
      .pipe(map((response) => this.modelConversionService.convertValidationResultVO(response)));
  }

  public calculateIO(projectVO: ProjectVO): Observable<IOConsumptionResultVO> {
    return this.wagoApiClient
      .post(this.calcIOConsumptionUrl, projectVO)
      .pipe(map((response) => this.modelConversionService.convertIOConsumptionResultVO(response)));
  }

  public async validateProjectAsync(): Promise<ValidationResultVO> {
    this.updateRepresentationOfCurrentProjectForValidationPurposes();
    const validationContextVO: ValidationContextVO = new ValidationContextVO(
      this.localeResourceManager.currentLanguage.langKey, this.projectModel.currentProjectType
    );
    validationContextVO.onlySingleArticleProposal = true;
    return await lastValueFrom(this.validateProject(this.projectModel.currentProject, validationContextVO, false));
  }

  public updateRepresentationOfCurrentProjectForValidationPurposes(): void {
    const currentProject: ProjectVO = this.projectModel.currentProject;
    if (!currentProject) {
      return;
    }

    if (!this.projectModel.view3dRepresentationForValidationPurposes) {
      this.projectModel.view3dRepresentationForValidationPurposes = this.createNewRepresentation(currentProject);
    }

    currentProject.representations = [this.projectModel.view3dRepresentationForValidationPurposes];
    ArticleContainerUtils.updateProjectWithGeometricData(currentProject, this.globalContainer);
  }

  private createNewRepresentation(currentProject: ProjectVO): RepresentationVO {
    const newRepresentation: RepresentationVO = new RepresentationVO();
    newRepresentation.type = RepresentationVO.TYPE_VIEW3D;
    newRepresentation.url = ProjectUtils.findFirstView3DRepresentationUrl(currentProject);
    newRepresentation.data = this.articleContainerUtils.createConfiguredObjRepresentationData(
      false,
      false,
      false,
      true,
      false
    );
    return newRepresentation;
  }
}
